import React, { useState, useEffect } from 'react';
import { Download, Package, Loader2, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Downloads = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/downloads/my-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await axios.get(`${API_URL}${fileUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md border-4 border-teal-300">
          <CardContent className="p-8 text-center">
            <Package className="h-16 w-16 text-teal-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Sign in to access your downloads</h2>
            <p className="text-gray-600 mb-6">View and download your purchased products</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                  Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              My Downloads
            </span>
          </h1>
          <p className="text-xl text-gray-600 font-medium">
            Access your purchased digital products
          </p>
        </div>

        {products.length === 0 ? (
          <Card className="border-4 border-teal-200">
            <CardContent className="py-16 text-center">
              <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-700 mb-2">No downloads yet</h2>
              <p className="text-gray-600 mb-6">Purchase a product to access downloads here.</p>
              <div className="space-x-4">
                <Link to="/book">
                  <Button className="bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                    <Package className="h-4 w-4 mr-2" />
                    Get the Book
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {products.map((product) => (
              <Card key={product.id} className="border-4 border-teal-200 hover:border-teal-400 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    {product.cover_image ? (
                      <img
                        src={product.cover_image}
                        alt={product.name}
                        className="w-24 h-32 object-cover rounded-lg shadow"
                      />
                    ) : (
                      <div className="w-24 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Package className="h-10 w-10 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900">{product.name}</h3>
                      <p className="text-gray-600 mt-1">{product.description}</p>
                      <p className="text-sm text-gray-500 mt-2">Category: {product.category}</p>
                    </div>
                    <div>
                      {product.file_url ? (
                        <Button
                          onClick={() => handleDownload(product.file_url, product.file_name)}
                          className="bg-gradient-to-r from-blue-500 to-teal-500 text-white"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      ) : (
                        <Button disabled variant="outline">
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Downloads;
